import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { createUser, listRoles, listUsers, type Role, type User, type UserCreate } from "@/api/client";
import { useSession } from "@/auth/session";
import { SearchBox } from "@/components/SearchBox";
import { formatDate } from "@/components/format";
import { Field, OtherProblems, inputClass, serverProblems, submitClass, useRevealProblems } from "@/components/form";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { passwordBytes, passwordProblem } from "@/features/users/password";
import type { UsersSearch } from "@/routes/users";
import { ScrollArea } from "@/components/ScrollArea";
import { PagedFooter } from "@/components/Pagination";
import { pageParams } from "@/routes/paging";

const usersApi = getRouteApi("/staff/users");

/** Roles to choose from, or null when the user can't read them. */
export function useRoleChoices() {
  const { can } = useSession();
  const allowed = can("role.read");
  const roles = useQuery({ queryKey: ["roles"], queryFn: listRoles, enabled: allowed });
  return { roles: allowed ? (roles.data ?? []) : null, error: roles.error };
}

export function roleNames(user: User) {
  const names = (user.roles ?? []).map((role) => role.name).filter(Boolean);
  return names.length > 0 ? names.join(", ") : "No roles";
}

export function UserList() {
  const search = usersApi.useSearch();
  const { q, role, active, page, page_size } = search;
  const { can } = useSession();
  const navigate = useNavigate();
  const { roles, error: rolesError } = useRoleChoices();
  const [creating, setCreating] = useState(false);

  const users = useQuery({
    queryKey: ["users", { q, role, active, page, page_size }],
    queryFn: () => {
      const params: Parameters<typeof listUsers>[0] = pageParams(search);
      if (q) params.q = q;
      if (role) params.roleId = role;
      if (active !== undefined) params.active = active;
      return listUsers(params);
    },
  });
  const items = users.data?.items ?? [];

  // Any filter change starts again from the first page.
  const filter = (next: Partial<UsersSearch>) =>
    void navigate({ to: "/users", search: { q, role, active, page_size, ...next, page: undefined } });

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Users"
        detail="Staff accounts and the roles they hold"
        action={
          can("user.manage") ? (
            <ToolbarButton disabled={creating} onClick={() => setCreating(true)}>
              New user
            </ToolbarButton>
          ) : null
        }
      />
      {creating ? <NewUser roles={roles} rolesError={rolesError} onCancel={() => setCreating(false)} /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox q={q} ariaLabel="Search users" placeholder="Name or email" onSearch={(text) => filter({ q: text || undefined })} />
        {roles ? (
          <select
            aria-label="Role"
            value={role ?? ""}
            onChange={(e) => filter({ role: e.target.value || undefined })}
            className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Any role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        ) : null}
        {rolesError ? <p className="text-xs text-red-700">Roles could not be loaded, so the role filter is unavailable.</p> : null}
        <select
          aria-label="Status"
          value={active === undefined ? "" : String(active)}
          onChange={(e) => filter({ active: e.target.value === "" ? undefined : e.target.value === "true" })}
          className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Any status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      <ErrorBanner error={users.error} />
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {users.isLoading ? (
          <EmptyState title="Loading users" detail="Fetching users." />
        ) : users.isSuccess && items.length === 0 ? (
          <EmptyState title="No users found" detail="Try a different name, email, role or status." />
        ) : (
          <ScrollArea label="Users">
            <table aria-label="Users" className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Roles</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/users/$userId"
                        params={{ userId: user.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {user.full_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{user.email}</td>
                    <td className="px-4 py-3 text-zinc-700">{roleNames(user)}</td>
                    <td className="px-4 py-3 text-zinc-700">{user.is_active ? "Active" : "Inactive"}</td>
                    <td className="px-4 py-3 text-zinc-500">{user.last_login_at ? formatDate(user.last_login_at) : "Never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
        <PagedFooter data={users.data} page={page} pageSize={page_size} />
      </div>

    </section>
  );
}

const USER_FIELDS = ["email", "full_name", "password", "role_ids"] as const;

function NewUser({ roles, rolesError, onCancel }: { roles: Role[] | null; rolesError: Error | null; onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: (body: UserCreate) => createUser(body),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.setQueryData(["user", created.id], created);
      await navigate({ to: "/users/$userId", params: { userId: created.id } });
    },
  });
  const edited = () => {
    if (create.error) create.reset();
  };

  const formRef = useRef<HTMLFormElement>(null);
  const reveal = useRevealProblems<"email" | "full_name" | "password">(formRef);
  const server = serverProblems(create.error, USER_FIELDS);
  const errors = {
    email: email.trim() === "" ? "is required" : undefined,
    full_name: fullName.trim() === "" ? "is required" : undefined,
    password: password === "" ? undefined : passwordProblem(password),
  };
  const blocked = Object.values(errors).some(Boolean);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (blocked) {
      reveal.submitted();
      return;
    }
    if (create.isPending) return;
    const body: UserCreate = { email: email.trim(), full_name: fullName.trim(), role_ids: roleIds };
    if (password !== "") body.password = password;
    create.mutate(body);
  }

  return (
    <section aria-labelledby="new-user" className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="new-user" className="text-sm font-semibold">
        New user
      </h2>
      <form ref={formRef} noValidate className="space-y-4" onSubmit={submit}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Email" problem={reveal.shown("email", errors.email) ?? server.byField.email}>
            {(props) => (
              <input
                {...props}
                type="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  edited();
                }}
                onBlur={() => reveal.touch("email")}
                className={inputClass}
              />
            )}
          </Field>
          <Field label="Full name" problem={reveal.shown("full_name", errors.full_name) ?? server.byField.full_name}>
            {(props) => (
              <input
                {...props}
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  edited();
                }}
                onBlur={() => reveal.touch("full_name")}
                className={inputClass}
              />
            )}
          </Field>
        </div>
        {rolesError ? (
          <p role="alert" className="text-sm text-red-700">
            Roles could not be loaded: {rolesError.message}. Reload before assigning roles.
          </p>
        ) : null}
        <RoleCheckboxes
          roles={roles}
          selected={roleIds}
          problem={server.byField.role_ids}
          onChange={(next) => {
            setRoleIds(next);
            edited();
          }}
          missingNote="You can't list roles (role.read), so the user starts with none; someone who can will assign them."
        />
        <Field
          label="Initial password"
          problem={reveal.shown("password", errors.password) ?? server.byField.password}
          hint={passwordHint(password, errors.password)}
        >
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                edited();
              }}
              onBlur={() => reveal.touch("password")}
              className={inputClass}
            />
          )}
        </Field>
        <ErrorBanner error={create.error} />
        <OtherProblems lines={server.other} />
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={create.isPending} className={submitClass}>
            {create.isPending ? "Creating..." : "Create user"}
          </button>
          <ToolbarButton disabled={create.isPending} onClick={onCancel}>
            Cancel
          </ToolbarButton>
        </div>
      </form>
    </section>
  );
}

function passwordHint(password: string, problem: string | undefined) {
  if (password === "") {
    return (
      <>
        Optional, 12 to 1024 bytes. <span>Without a password the account can't sign in until someone sets one.</span>
      </>
    );
  }
  return problem ? undefined : `${passwordBytes(password)} bytes`;
}

/** One checkbox per role; without role.read there is nothing to list, so `missingNote` explains. */
export function RoleCheckboxes({
  roles,
  selected,
  problem,
  onChange,
  missingNote,
}: {
  roles: Role[] | null;
  selected: string[];
  problem?: string;
  onChange: (next: string[]) => void;
  missingNote: string;
}) {
  if (roles === null) return <p className="text-xs text-zinc-500">{missingNote}</p>;
  return (
    <fieldset className="text-sm">
      <legend className="font-medium text-zinc-700">Roles</legend>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        {roles.map((role) => (
          <label key={role.id} className="flex items-center gap-2 text-zinc-700">
            <input
              type="checkbox"
              checked={selected.includes(role.id)}
              onChange={(e) => onChange(e.target.checked ? [...selected, role.id] : selected.filter((id) => id !== role.id))}
            />
            {role.name}
          </label>
        ))}
      </div>
      {problem ? <p className="mt-1 text-xs text-red-700">{problem}</p> : null}
    </fieldset>
  );
}
